import React, { useEffect, useMemo, useState } from "react";
import { API_BASE, fetchWithAuth } from "../utils/api";

function ManagementPanel({ rows, userRole }) {
  const [activeTab, setActiveTab] = useState("drivers"); // drivers | vehicles | dispatch
  const [options, setOptions] = useState({ laiXe: [], soXe: [], dieuPhoi: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newItem, setNewItem] = useState("");
  const [editingValue, setEditingValue] = useState(null); // string
  const [editingDraft, setEditingDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const normalizedRole = userRole?.toLowerCase();
  const canManage = normalizedRole === "dispatchers" || normalizedRole === "admin";

  const key =
    activeTab === "drivers"
      ? "laiXe"
      : activeTab === "vehicles"
      ? "soXe"
      : "dieuPhoi";

  const loadMasterData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/revenue/master-data`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Không thể tải danh mục");
      setOptions(data.data || { laiXe: [], soXe: [], dieuPhoi: [] });
    } catch (e) {
      setError(e.message || "Có lỗi xảy ra");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchFromRows = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((r) => {
      if (r?.dieuPhoi) set.add(String(r.dieuPhoi).trim());
    });
    return Array.from(set)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "vi"));
  }, [rows]);

  const list =
    key === "dieuPhoi" && (!options.dieuPhoi || options.dieuPhoi.length === 0)
      ? dispatchFromRows
      : options[key] || [];

  const title =
    activeTab === "drivers"
      ? "Danh sách lái xe"
      : activeTab === "vehicles"
      ? "Danh sách số xe"
      : "Danh sách điều phối";

  const resetEditing = () => {
    setEditingValue(null);
    setEditingDraft("");
  };

  const handleAdd = async () => {
    const v = (newItem || "").trim();
    if (!v) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/revenue/master-data/${key}`, {
        method: "POST",
        body: JSON.stringify({ value: v }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Không thể thêm");
      setOptions((prev) => ({ ...prev, [key]: data.data || [] }));
      setNewItem("");
    } catch (e) {
      setError(e.message || "Có lỗi xảy ra");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (val) => {
    setEditingValue(val);
    setEditingDraft(val);
  };

  const handleSaveEdit = async () => {
    const oldValue = (editingValue || "").trim();
    const newValue = (editingDraft || "").trim();
    if (!oldValue || !newValue) return;
    if (oldValue === newValue) return resetEditing();
    setSaving(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/revenue/master-data/${key}`, {
        method: "PUT",
        body: JSON.stringify({ oldValue, newValue }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Không thể sửa");
      setOptions((prev) => ({ ...prev, [key]: data.data || [] }));
      resetEditing();
    } catch (e) {
      setError(e.message || "Có lỗi xảy ra");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (val) => {
    const v = (val || "").trim();
    if (!v) return;
    if (!window.confirm(`Xóa "${v}" khỏi danh mục?`)) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/revenue/master-data/${key}`, {
        method: "DELETE",
        body: JSON.stringify({ value: v }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Không thể xóa");
      setOptions((prev) => ({ ...prev, [key]: data.data || [] }));
      if (editingValue && editingValue.toLowerCase() === v.toLowerCase()) {
        resetEditing();
      }
    } catch (e) {
      setError(e.message || "Có lỗi xảy ra");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Quản lý</h1>
        <p className="text-sm text-slate-600">
          Quản lý danh mục lái xe, số xe và điều phối. Khi thêm/sửa/xóa, dropdown
          trong Google Sheet sẽ được cập nhật.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab("drivers")}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold border ${
              activeTab === "drivers"
                ? "bg-blue-50 border-blue-200 text-blue-900"
                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            Danh sách lái xe
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("vehicles")}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold border ${
              activeTab === "vehicles"
                ? "bg-blue-50 border-blue-200 text-blue-900"
                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            Danh sách số xe
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("dispatch")}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold border ${
              activeTab === "dispatch"
                ? "bg-blue-50 border-blue-200 text-blue-900"
                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            Danh sách điều phối
          </button>
        </div>

        {error ? (
          <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
            {error}
          </div>
        ) : null}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <h2 className="text-sm sm:text-base font-semibold text-slate-900">
            {title}
          </h2>
          <span className="text-xs text-slate-500">
            Tổng: {Array.isArray(list) ? list.length : 0}
            {loading ? " • Đang tải..." : ""}
          </span>
        </div>

        {canManage && (
          <div className="flex flex-col sm:flex-row gap-2 mb-3 sm:mb-4">
            <input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Nhập giá trị mới..."
              className="flex-1 rounded-md border border-slate-200 px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving || !newItem.trim()}
              className="w-full sm:w-auto rounded-md bg-blue-600 text-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              Thêm
            </button>
            <button
              type="button"
              onClick={loadMasterData}
              disabled={saving}
              className="w-full sm:w-auto rounded-md border border-slate-200 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Làm mới
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {(list || []).map((item, idx) => {
            const isEditing = editingValue === item;
            return (
              <div
                key={`${item}-${idx}`}
                className="rounded-lg border border-slate-200 bg-slate-50 px-2 sm:px-3 py-2 text-xs sm:text-sm text-slate-800 flex items-center justify-between gap-2"
                title={item}
              >
                {isEditing ? (
                  <input
                    value={editingDraft}
                    onChange={(e) => setEditingDraft(e.target.value)}
                    className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-xs sm:text-sm bg-white focus:outline-none focus:ring-1 focus:ring-slate-400"
                    disabled={saving}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveEdit();
                      if (e.key === "Escape") resetEditing();
                    }}
                  />
                ) : (
                  <span className="truncate flex-1 text-xs sm:text-sm">
                    {item}
                  </span>
                )}

                {canManage ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={handleSaveEdit}
                          disabled={saving || !editingDraft.trim()}
                          className="text-xs font-semibold text-emerald-700 px-1.5 sm:px-2 py-1 rounded hover:bg-emerald-50 disabled:opacity-50 whitespace-nowrap"
                        >
                          Lưu
                        </button>
                        <button
                          type="button"
                          onClick={resetEditing}
                          disabled={saving}
                          className="text-xs font-semibold text-slate-700 px-1.5 sm:px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50 whitespace-nowrap"
                        >
                          Hủy
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          disabled={saving}
                          className="text-xs font-semibold text-blue-700 px-1.5 sm:px-2 py-1 rounded hover:bg-blue-50 disabled:opacity-50 whitespace-nowrap"
                        >
                          Sửa
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          disabled={saving}
                          className="text-xs font-semibold text-rose-700 px-1.5 sm:px-2 py-1 rounded hover:bg-rose-50 disabled:opacity-50 whitespace-nowrap"
                        >
                          Xóa
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}

          {(list || []).length === 0 && !loading && (
            <div className="col-span-full text-sm text-slate-500">
              Chưa có dữ liệu.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManagementPanel;

